import React, { useEffect, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/react-hooks';
import _ from 'lodash';
import styles from './Accuracy.module.scss';
import { GET_ACCURACIES, GET_FILTERS_DATA, GET_CLASS_TREE_DATA } from './query';
import LineChart from './LineChart';
import numbro from 'numbro';
import BarChart from './BarChart';
import InfoTooltip from '../../components/InfoTooltip';

const collectionOptions = [
  { value: 'c5', label: '5' },
  { value: 'c6', label: '6' },
  { value: 'c7', label: '7' },
  { value: 'c8', label: '8' },
  { value: 'c9', label: '9' },
];

export default function Accuracy() {
  const [collection, setCollection] = useState('c9');
  const [state, setState] = useState({
    level: 'l1',
    biome: 'Brasil',
    year: 2022,
  });
  const { data: filtersData, loading: loadingFiltersData } = useQuery(GET_FILTERS_DATA, {
    variables: {
      collection
    }
  });
  const biomes = _.get(filtersData, 'accuracyBiomes', []);
  const levels = _.get(filtersData, 'accuracyLevels', []);
  const years = _.get(filtersData, 'accuracyYears', []);
  const { data: classTree } = useQuery(GET_CLASS_TREE_DATA, {
    variables: {
      classTreeKey: 'default',
    },
  });
  const [getAccuracies, { data: accuracies, loading: loadingAccuracies }] = useLazyQuery(GET_ACCURACIES);

  useEffect(() => {
    getAccuracies({ variables: {
      collection,
      ...state
    } });
  }, [state.year]);

  useEffect(() => {
    if (!loadingFiltersData) {
      const {
        level,
        biome,
      } = state;
      let nextState = {
        ...state,
        year: _.last(years)
      };

      if (!_.includes(biomes, biome)) {
        nextState['biome'] = 'Brasil';
      }

      if (!_.includes(levels, level)) {
        nextState['level'] = 'l2';
      }

      setState(nextState);
      getAccuracies({ variables: {
        collection,
        ...nextState
      } });
    }
  }, [collection, loadingFiltersData]);

  const handleSubmit = () => {
    getAccuracies({ variables: {
      collection,
      ...state
    } });
  };

  const handleChange = (event) => {
    const { id, value } = event.target;
    setState({ ...state, [id]: id === 'year' ? Number(value) : value });
  };

  const handleCollectionChange = (event) => {
    const { value } = event.target;
    setCollection(value);
  };

  const legend = _.keyBy(_.get(classTree, 'classTreeByKey[0].mvClassTreeLevelsUi', []), 'pixelValue');
  const overallAccuracy = _.get(accuracies, 'accuracies.overallAccuracy', []);
  const userAccuracy = _.get(accuracies, 'accuracies.userAccuracy', []);
  const producerAccuracy = _.get(accuracies, 'accuracies.producerAccuracy', []);
  
  // const confusionProdLeft = _.groupBy(_.get(accuracies, 'accuracies.confusionProdLeft', []), 'classId');
  const confusionProdLeft = _.flow([
    () => _.get(accuracies, 'accuracies.confusionProdLeft', []),
    data => _.groupBy(data, 'classId'),
    grouped => {
      const ordered = {};
      Object.keys(grouped)
        .sort((a, b) => Number(a) - Number(b))
        .forEach(key => {
          ordered[key] = grouped[key];
        });
      return ordered;
    }
  ])();

  const countProdLeftSeries = _.get(Object.values(confusionProdLeft), '[0].length', 0);
  const confusionProdRight = _.filter(_.get(accuracies, 'accuracies.confusionProdRight', []), (item) => item.classId !== -1).sort((a, b) => a.classId - b.classId);

  // const confusionUserLeft = _.groupBy(_.get(accuracies, 'accuracies.confusionUserLeft', []), 'classId');
  const confusionUserLeft = _.flow([
    () => _.get(accuracies, 'accuracies.confusionUserLeft', []),
    data => _.groupBy(data, 'classId'),
    grouped => {
      const ordered = {};
      Object.keys(grouped)
        .sort((a, b) => Number(a) - Number(b))
        .forEach(key => {
          ordered[key] = grouped[key];
        });
      return ordered;
    }
  ])();

  const countUserLeftSeries = _.get(Object.values(confusionUserLeft), '[0].length', 0);
  const confusionUserRight = _.filter(_.get(accuracies, 'accuracies.confusionUserRight', []), (item) => item.classId !== -1).sort((a, b) => a.classId - b.classId);

  const omissionCategories = _.map(confusionProdRight, (item) => _.get(legend, `${item.classId}.defaultName`, item.classId));
  const insertionCategories = _.map(confusionUserRight, (item) => _.get(legend, `${item.classId}.defaultName`, item.classId));
  
  const omissionSeries = [
    {
      data: _.map(confusionProdRight, (item) => ({
        value: Number(item.value).toFixed(1),
        itemStyle: {
          color: _.get(legend, `${item.classId}.color`),
        }
      })),
    }
  ];
  for (let i = 0; i < countProdLeftSeries; i++) {
    omissionSeries.push({
      data: _.map(confusionProdLeft, (item) => ({
        value: Number(item[i].value).toFixed(1) * -1,
        itemStyle: {
          color: _.get(legend, `${item[i].idClassMap}.color`),
        }
      })),
    });
  }

  const insertionSeries = [
    {
      data: _.map(confusionUserRight, (item) => ({
        value: Number(item.value).toFixed(1),
        itemStyle: {
          color: _.get(legend, `${item.classId}.color`),
        }
      })),
    }
  ];
  for (let i = 0; i < countUserLeftSeries; i++) {
    insertionSeries.push({
      data: _.map(confusionUserLeft, (item) => ({
        value: Number(item[i].value).toFixed(1) * -1,
        itemStyle: {
          color: _.get(legend, `${item[i].idClassMap}.color`),
        }
      })),
    });
  }

  const overallYears = _.map(overallAccuracy, 'year');
  const overallData = [{ 
    name: 'Valor',
    color: '#00A00D',
    data: _.map(overallAccuracy, (item) => Number(item.value).toFixed(1)) 
  }];
  const userYears = _.map(_.get(userAccuracy, '[0].statistics'), 'year');
  const userData = _.map(
    userAccuracy,
    (item) => ({
      name: _.get(legend, `${item.classId}.defaultName`, item.classId),
      color: _.get(legend, `${item.classId}.color`),
      data: _.map(item.statistics, (item) => Number(item.value).toFixed(1)),
    })
  );
  const producerYears = _.map(_.get(producerAccuracy, '[0].statistics'), 'year');
  const producerData = _.map(
    producerAccuracy,
    (item) => ({
      name: _.get(legend, `${item.classId}.defaultName`, item.classId),
      color: _.get(legend, `${item.classId}.color`),
      data: _.map(item.statistics, (item) => Number(item.value).toFixed(1)),
    })
  );
  
  return (
    <div className={ styles.container }>
      {
        loadingAccuracies && (
          <div className={ styles.loadingContainer }>
            <div className='lds-ring'>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </div>
          </div>
        )
      }
      <div className={ styles.searchIntro }>
        <p>Selecione o nível e o bioma nas caixas abaixo para visualização dos resultados estatísticos de acurácia da Coleção</p>
        <select id='collection' onChange={ handleCollectionChange } value={ collection } className={ styles.select }>
          {
            collectionOptions.map(({ value, label }) => (
              <option value={ value }>{ label }</option>
            ))
          }
        </select>
      </div>
      <div className={ styles.searchContainer }>
        <div className={ styles.formControl }>
          <label for='biome' className={ styles.label }>Bioma</label>
          <select id='biome' onChange={ handleChange } value={ state.biome } className={ styles.select }>
            {
              _.map(biomes, (biome) => (
                <option value={ biome }>{ biome }</option>
              ))
            }
          </select>
        </div>
        <div className={ styles.formControl }>
          <label for='level' className={ styles.label }>Nível</label>
          <select id='level' onChange={ handleChange } value={ state.level } className={ styles.select }>
            {
              _.map(levels, (level) => (
                level !== 'l3' && <option value={ level }>Nível { level.replace(/\D/, '') }</option>
              ))
            }
          </select>
        </div>
        <div className={ styles.buttonContainer }>
          <button onClick={ handleSubmit } type='button' className={ styles.button }>Buscar</button>
        </div>
      </div>

      <div className={ styles.cards }>
        <div className={ styles.card }>
          <span>Acurácia geral</span>
          <span className={ styles.cardData }>{ numbro(_.get(accuracies, 'accuracies.globalAccuracy', 0)).format({ mantissa: 2}) }%</span>
        </div>
        <div className={ styles.card }>
          <span>Discordância de alocação</span>
          <span className={ styles.cardData }>{ numbro(_.get(accuracies, 'accuracies.allocationTot', 0)).format({ mantissa: 2}) }%</span>
        </div>
        <div className={ styles.card }>
          <span>Discordância de quantidade</span>
          <span className={ styles.cardData }>{ numbro(_.get(accuracies, 'accuracies.quantityTot', 0)).format({ mantissa: 2}) }%</span>
        </div>
      </div>

      <div className={ styles.lineCharts }>
        <div className={ styles.chart }>
          <p>
            Acurácia Global
            <InfoTooltip text='A acurácia global (AG), é uma medida percentual da quantidade de amostras corretamente classificadas, em relação ao total de amostras disponíveis. (fonte: https://solved.eco.br/avaliacao-de-acuracia-ou-concordancia/)' />
          </p>
          <LineChart categories={ overallYears } series={ overallData } />
        </div>
        <div className={ styles.chart }>
          <p>
            Erro de Inclusão
            <InfoTooltip text='O Erro de Inclusão (ou Erro de Comissão) nos informa a quantidade de classificações associadas a falsos positivos ou falsos alarmes. É o complemento da acurácia do consumidor (AC), ou do usuário, é a medida de acurácia do ponto de vista do usuário/consumidor do mapa. Aqui, a acurácia basicamente diz ao usuário com que frequência a classe do mapa estará realmente presente no solo ou em sua referência. É também conhecida como medida de confiabilidade (reliability).' />
          </p>
          <LineChart categories={ userYears } series={ userData } />
        </div>
        <div className={ styles.chart }>
          <p>
            Erro de Omissão
            <InfoTooltip text='O Erro de Omissão informa a quantidade percentual de erros de omissões, ou seja, classes que deveriam ter sido detectadas, mas que por alguma razão foram omitidas da classificação. É o complemento da acurácia do produtor (AP), que é a acurácia do ponto de vista do criador/produtor do mapa. Esta é a frequência com que as características reais no solo são mostradas corretamente no mapa classificado ou a probabilidade de uma determinada cobertura/uso da terra ser classificada como tal no mapa.' />
          </p>
          <LineChart categories={ producerYears } series={ producerData } />
        </div>
      </div>

      <div className={ styles.formControl }>
        <label for='year' className={ styles.label }>Ano</label>
        <select id='year' onChange={ handleChange } value={ state.year } className={ styles.select }>
          {
            _.map(years, (year) => (
              <option value={ year }>{ year }</option>
            ))
          }
        </select>
      </div>

      <div className={ styles.barChart }>
        <p>Erros de inclusão | Acurácia do Usuário</p>
        <BarChart categories={ insertionCategories } series={ insertionSeries } />
      </div>
      <div className={ styles.barChart }>
        <p>Erros de omissão | Acurácia do Produtor</p>
        <BarChart categories={ omissionCategories } series={ omissionSeries } />
      </div>
    </div>
  );
}